import { motion } from "framer-motion";
import { ValidAnimation } from "./animations/ValidAnimation";

interface ProductHeroAnimationProps {
  productKey: string;
}

function AtlasAnimation() {
  const zones = [
    // Teal — left column
    { x: 10, y: 10, w: 135, h: 115, rx: 10, fill: "hsl(186 60% 40% / 0.25)", stroke: "hsl(186 60% 60% / 0.6)", postcode: "114 51", delay: 0.3 },
    { x: 10, y: 129, w: 135, h: 115, rx: 10, fill: "hsl(186 60% 40% / 0.20)", stroke: "hsl(186 60% 60% / 0.5)", postcode: "114 56", delay: 0.4 },
    // Coral — middle column
    { x: 149, y: 10, w: 145, h: 115, rx: 10, fill: "hsl(0 100% 70% / 0.10)", stroke: "hsl(0 100% 70% / 0.35)", postcode: "115 21", delay: 0.5 },
    { x: 149, y: 129, w: 145, h: 115, rx: 10, fill: "hsl(0 100% 70% / 0.07)", stroke: "hsl(0 100% 70% / 0.28)", postcode: "115 28", delay: 0.6 },
    // Turkos — right column
    { x: 298, y: 10, w: 132, h: 115, rx: 10, fill: "hsl(181 100% 33% / 0.10)", stroke: "hsl(181 100% 33% / 0.35)", postcode: "116 40", delay: 0.7 },
    { x: 298, y: 129, w: 132, h: 115, rx: 10, fill: "hsl(181 100% 33% / 0.07)", stroke: "hsl(181 100% 33% / 0.25)", postcode: "116 45", delay: 0.8 },
  ];

  // Pins placed in center of specific zones (offset from postcode text)
  const pins = [
    { cx: 77, cy: 82, delay: 1.0 },    // zone 1 lower area
    { cx: 221, cy: 82, delay: 1.2 },    // zone 3 lower area
    { cx: 364, cy: 200, delay: 1.4 },   // zone 6 lower area
    { cx: 77, cy: 200, delay: 1.6 },    // zone 2 lower area
  ];

  const roads = [
    "M10 125 Q220 120, 430 125",
    "M145 10 Q147 130, 149 244",
    "M294 10 Q296 130, 298 244",
  ];

  const legend = [
    { color: "hsl(186 60% 60%)", label: "Område 1" },
    { color: "hsl(0 100% 70%)", label: "Område 2" },
    { color: "hsl(181 100% 33%)", label: "Område 3" },
  ];

  return (
    <div className="relative w-full max-w-lg">
      <svg viewBox="0 0 440 260" className="w-full" fill="none">
        {/* Subtle road lines */}
        {roads.map((d, i) => (
          <motion.path key={`road-${i}`} d={d} stroke="hsl(186 100% 18.4% / 0.06)" strokeWidth={0.5} strokeDasharray="4 6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 + i * 0.05 }} />
        ))}

        {/* Zone rectangles */}
        {zones.map((zone, i) => (
          <motion.g key={`zone-${i}`}>
            <motion.rect
              x={zone.x} y={zone.y} width={zone.w} height={zone.h} rx={zone.rx}
              fill={zone.fill} stroke={zone.stroke} strokeWidth={1.5}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: zone.delay }}
            />
            <motion.text
              x={zone.x + zone.w / 2} y={zone.y + zone.h * 0.38}
              textAnchor="middle" fontSize="12" fontWeight="600"
              fill="hsl(0 0% 100% / 0.65)"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ delay: zone.delay + 0.3 }}
            >
              {zone.postcode}
            </motion.text>
          </motion.g>
        ))}

        {/* Map pins with pulse */}
        {pins.map((pin, i) => (
          <motion.g key={`pin-${i}`}>
            <motion.circle
              cx={pin.cx} cy={pin.cy} r={10}
              fill="hsl(0 100% 70% / 0.12)"
              initial={{ scale: 0 }}
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ delay: pin.delay + 0.2, duration: 2, repeat: Infinity }}
              style={{ transformOrigin: `${pin.cx}px ${pin.cy}px` }}
            />
            <motion.circle
              cx={pin.cx} cy={pin.cy} r={5}
              fill="hsl(0 100% 70%)" stroke="hsl(0 0% 100%)" strokeWidth={1.5}
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 15, delay: pin.delay }}
              style={{ transformOrigin: `${pin.cx}px ${pin.cy}px` }}
            />
          </motion.g>
        ))}
      </svg>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-3">
        {legend.map((item, i) => (
          <motion.div key={i} className="flex items-center gap-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 + i * 0.1 }}>
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-xs text-white/60 font-body">{item.label}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function AdresstwattAnimation() {
  const rows = [
    { address: "Storgatan 12, 114 51 Stocholm", corrected: "Storgatan 12, 114 51 Stockholm" },
    { address: "Kungsgtan 5, 411 19 Götebrog", corrected: "Kungsgatan 5, 411 19 Göteborg" },
    { address: "Drottningg. 8, 21148 Malmö", corrected: "Drottninggatan 8, 211 48 Malmö" },
    { address: "Västra Hamng 22, 41117 Gbg", corrected: "Västra Hamngatan 22, 411 17 Göteborg" },
  ];

  return (
    <div className="relative w-full max-w-md">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="px-4 py-2.5 border-b border-white/10 flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
            <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
            <div className="w-2.5 h-2.5 rounded-full bg-white/20" />
          </div>
          <span className="text-xs text-white/40 font-body ml-2">adresstvätt.csv</span>
        </div>

        {/* Rows */}
        <div className="divide-y divide-white/5">
          {rows.map((row, i) => (
            <motion.div
              key={i}
              className="px-4 py-3 flex items-center gap-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 + i * 0.2 }}
            >
              {/* Status icon */}
              <div className="relative flex-shrink-0 w-6 h-6">
                <motion.div
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 0 }}
                  transition={{ delay: 1.5 + i * 0.4, duration: 0.2 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20, delay: 1.7 + i * 0.4 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>
              </div>

              {/* Address text */}
              <div className="flex-1 min-w-0">
                <div className="relative">
                  <motion.span
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 0 }}
                    transition={{ delay: 1.5 + i * 0.4, duration: 0.2 }}
                    className="text-sm text-white/50 font-body line-through decoration-red-400/40 absolute inset-0"
                  >
                    {row.address}
                  </motion.span>
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.7 + i * 0.4, duration: 0.3 }}
                    className="text-sm text-white/90 font-body"
                  >
                    {row.corrected}
                  </motion.span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="px-4 py-3 border-t border-white/10">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-white/40 font-body">Bearbetar…</span>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 3.2 }}
              className="text-xs text-emerald-400 font-body"
            >
              4/4 klara
            </motion.span>
          </div>
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-coral to-emerald-400 rounded-full"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 2, delay: 1.2, ease: "easeInOut" }}
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export function ProductHeroAnimation({ productKey }: ProductHeroAnimationProps) {
  switch (productKey) {
    case "valid":
      return <ValidAnimation />;
    case "atlas":
      return <AtlasAnimation />;
    case "adresstwatt":
      return <AdresstwattAnimation />;
    default:
      return null;
  }
}
